import React, { useMemo } from "react";
import { PlayerRow } from "./PlayerRow";
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';

export const PlayerTable = ({
  data = [],
  loading = false,
  pagination = null,
  onLoadMore = null,
  limit = 20,
  onLimitChange = null,
  selectedStatColumns = [],
  filterActiveTab = null,
  sortColumn = null,
  sortDirection = 'asc',
  onSort = null
}) => {

  // Define stat column mappings (same as coach)
  const statColumnMapping = {
    // Hitting stats
    'avg': { label: 'AVG', key: 'avg' },
    'obp': { label: 'OBP', key: 'ob_pct' },
    'slg': { label: 'SLG', key: 'slg_pct' },
    'ops': { label: 'OPS', key: 'ops', calculated: true },
    'ops+': { label: 'OPS+', key: 'ops_plus' },
    'runs': { label: 'R', key: 'r' },
    'hits': { label: 'H', key: 'h' },
    '2b': { label: '2B', key: '2b' },
    '3b': { label: '3B', key: '3b' },
    'hr': { label: 'HR', key: 'hr' },
    'rbi': { label: 'RBI', key: 'rbi' },
    'tb': { label: 'TB', key: 'tb' },
    'bb': { label: 'BB', key: 'bb' },
    'k': { label: 'K', key: 'so' },
    'k%': { label: 'K%', key: 'k%' },
    'bb%': { label: 'BB%', key: 'bb%' },
    'k/bb': { label: 'K/BB', key: 'k_bb', calculated: true },
    'sb': { label: 'SB', key: 'sb' },
    'cs': { label: 'CS', key: 'cs' },
    // Fielding stats
    'fld%': { label: 'FLD%', key: 'fielding_percentage', isFielding: true },
    'e': { label: 'E', key: 'errors', isFielding: true },
    'po': { label: 'PO', key: 'putouts', isFielding: true },
    'a': { label: 'A', key: 'assists', isFielding: true },
    'dp': { label: 'DP', key: 'double_plays', isFielding: true },
    'tc': { label: 'TC', key: 'total_chances', isFielding: true },
    'sba': { label: 'SBA', key: 'stolen_bases_against', isFielding: true },
    'rcs': { label: 'RCS', key: 'runners_caught_stealing', isFielding: true },
    'rcs%': { label: 'RCS%', key: 'runners_caught_stealing_percentage', isFielding: true },
    'pb': { label: 'PB', key: 'passed_balls', isFielding: true },
    // Pitching stats
    'w': { label: 'W', key: 'wins' },
    'l': { label: 'L', key: 'losses' },
    'era': { label: 'ERA', key: 'era' },
    'whip': { label: 'WHIP', key: 'WHIP' },
    'ip': { label: 'IP', key: 'innings_pitched' },
    'so': { label: 'SO', key: 'strikeouts_pitched' },
    'gs': { label: 'GS', key: 'games_started' },
    'app': { label: 'APP', key: 'games_pitched' },
    'cg': { label: 'CG', key: 'complete_games' },
    'sho': { label: 'SHO', key: 'shutouts' },
    'sv': { label: 'SV', key: 'saves' },
    'h': { label: 'H', key: 'hits_allowed' },
    'r': { label: 'R', key: 'runs_allowed' },
    'er': { label: 'ER', key: 'earned_runs' },
    'baa': { label: 'BAA', key: 'batting_average_against' },
    'ab': { label: 'AB', key: 'at_bats' },
    'wp': { label: 'WP', key: 'wild_pitches' },
    'hbp': { label: 'HBP', key: 'hit_batters' },
    'bk': { label: 'BK', key: 'balks' },
    'iso': { label: 'ISO', key: 'iso' },
    'woba': { label: 'wOBA', key: 'woba' },
    'wrc+': { label: 'wRC+', key: 'wrc_plus' },
    'pa': { label: 'PA', key: 'pa' },
    'babip': { label: 'BABIP', key: 'babip' },
    'fip': { label: 'FIP', key: 'fip' },
    'k-bb%': { label: 'K-BB%', key: 'k_bb_pct' },
    'fip+': { label: 'FIP+', key: 'fip_plus' },
    'era+': { label: 'ERA+', key: 'era_plus' },
    'xfip': { label: 'xFIP', key: 'xfip' },
    'lob%': { label: 'LOB%', key: 'lob_pct' },
    'babip-against': { label: 'BABIP Against', key: 'babip_against' },
  };

  // Mapping from UI column key to backend sortBy field name
  const columnToBackendSort = {
    'no': 'jerseyNumber',
    'name': 'firstName',
    'position': 'position',
    'class': 'playerClass',
    'height': 'height',
    'weight': 'weight',
    'batThrow': 'batsThrows',
    'playerScore': 'finalScore',
  };

  // Mapping from stat column ID to backend sortBy field name
  const statToBackendSort = {
    'avg': 'batting_average',
    'obp': 'on_base_percentage',
    'slg': 'slugging_percentage',
    'ops': 'on_base_plus_slugging',
    'runs': 'runs',
    'hits': 'hits',
    '2b': 'doubles',
    '3b': 'triples',
    'hr': 'home_runs',
    'rbi': 'rbi',
    'tb': 'total_bases',
    'bb': 'walks',
    'k': 'strikeouts',
    'k%': 'strikeout_percentage',
    'bb%': 'walk_percentage',
    'sb': 'stolen_bases',
    'cs': 'caught_stealing',
    'gp': 'games_played',
    'gs_hitting': 'games_started',
    'era': 'era',
    'app': 'appearances',
    'w': 'wins',
    'l': 'losses',
    'so': 'strikeouts_pitched',
    'ip': 'innings_pitched',
    'whip': 'WHIP',
    'cg': 'complete_games',
    'sho': 'shutouts',
    'sv': 'saves',
    'h': 'hits_allowed',
    'r': 'runs_allowed',
    'er': 'earned_runs',
    'baa': 'batting_average_against',
    'ab': 'at_bats',
    'wp': 'wild_pitches',
    'hbp': 'hit_batters',
    'bk': 'balks',
    'gs': 'games_started',
    'fld%': 'fielding_percentage',
    'e': 'errors',
    'po': 'putouts',
    'a': 'assists',
    'dp': 'double_plays',
    'tc': 'total_chances',
    'pb': 'passed_balls',
    'sba': 'f_stolen_bases_against',
    'rcs': 'runners_caught_stealing',
    'rcs%': 'runners_caught_stealing_percentage',
  };

  // Determine dynamic stat columns based on filters (include statId for sorting)
  const dynamicStatColumns = useMemo(() => {
    if (selectedStatColumns.length > 0) {
      return selectedStatColumns.map(col => {
        const mapping = statColumnMapping[col];
        return mapping ? { ...mapping, statId: col } : null;
      }).filter(Boolean);
    }
    return [];
  }, [selectedStatColumns]);

  const formatStat = (value, columnKey) => {
    if (value === null || value === undefined || value === '') return '0';
    if (typeof value === 'number') {
      if (value === 0) return '0';
      if (columnKey === 'ops_plus') return Math.round(value).toString();
      if (columnKey === 'WHIP' || columnKey === 'whip') return value.toFixed(2);
      return value % 1 === 0 ? value : value.toFixed(3);
    }
    return value;
  };

  const calculateOPS = (obp, slg) => {
    const obpVal = parseFloat(obp) || 0;
    const slgVal = parseFloat(slg) || 0;
    return (obpVal + slgVal).toFixed(3);
  };

  // Helper function to get stat value from player data
  const getStatValue = (player, column) => {
    const currentTab = filterActiveTab;
    let stats;

    // Check if this is a fielding stat
    if (column.isFielding) {
      stats = Array.isArray(player.fieldingStats)
        ? player.fieldingStats[0]
        : player.fieldingStats;
    } else if (currentTab === 'hitting') {
      stats = Array.isArray(player.battingStats)
        ? player.battingStats[0]
        : player.battingStats;
    } else {
      stats = Array.isArray(player.pitchingStats)
        ? player.pitchingStats[0]
        : player.pitchingStats;
    }

    // K/BB = K / BB (strikeouts / walks)
    if (column.calculated && column.key === 'k_bb') {
      const k = parseFloat(stats?.so ?? stats?.strikeouts ?? stats?.strikeouts_pitched ?? 0);
      const bb = parseFloat(stats?.bb ?? stats?.walks ?? stats?.walks_allowed ?? 0);
      if (bb > 0) return (k / bb).toFixed(2);
      return k > 0 ? '∞' : '0';
    }

    // K% and BB% - read directly from backend
    const fmtPct = (v) => {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return n > 1 ? `${n.toFixed(1)}%` : `${(n * 100).toFixed(1)}%`;
    };
    if (column.key === 'k%') {
      const v = currentTab === 'pitching' ? stats?.K_percent : stats?.k_percentage;
      return fmtPct(v);
    }
    if (column.key === 'bb%') {
      const v = currentTab === 'pitching' ? stats?.BB_percent : stats?.bb_percentage;
      return fmtPct(v);
    }
    if (column.key === 'k_bb_pct') {
      const v = stats?.K_BB_percent;
      return fmtPct(v);
    }

    // Calculate OPS if needed
    if (column.calculated && column.key === 'ops') {
      const obp = stats?.ob_pct || stats?.onBasePercentage;
      const slg = stats?.slg_pct || stats?.sluggingPercentage;
      return calculateOPS(obp, slg);
    }

    // Context-aware key mapping for pitching stats
    let actualKey = column.key;
    if (currentTab === 'pitching') {
      const pitchingKeyMapping = {
        '2b': 'doubles_allowed',
        '3b': 'triples_allowed',
        'hr': 'home_runs_allowed',
        'bb': 'walks_allowed',
      };
      actualKey = pitchingKeyMapping[column.key] || column.key;
    }

    // Define fallback mappings
    const fallbackMapping = {
      'avg': ['avg', 'battingAverage', 'batting_average'],
      'gp': ['gp', 'games_played', 'gamesPlayed'],
      'gs': ['gs', 'games_started', 'gamesStarted'],
      'ab': ['ab', 'atBats', 'at_bats'],
      'r': ['r', 'runs'],
      'h': ['h', 'hits'],
      '2b': ['2b', 'doubles'],
      '3b': ['3b', 'triples'],
      'hr': ['hr', 'home_runs', 'homeRuns'],
      'rbi': ['rbi'],
      'tb': ['tb', 'totalBases', 'total_bases'],
      'slg_pct': ['slg_pct', 'sluggingPercentage', 'slugging_percentage'],
      'bb': ['bb', 'walks', 'bb%'],
      'k%': ['k%'],
      'so': ['so', 'strikeouts'],
      'ob_pct': ['ob_pct', 'onBasePercentage', 'on_base_percentage'],
      'sb': ['sb', 'stolenBases', 'stolen_bases'],
      'cs': ['cs', 'caughtStealing', 'caught_stealing'],
      'wins': ['w', 'wins'],
      'losses': ['l', 'losses'],
      'era': ['era', 'ERA'],
      'games_pitched': ['gp', 'games_pitched', 'gamesPitched', 'app', 'appearances'],
      'games_started': ['gs', 'games_started', 'gamesStarted'],
      'complete_games': ['cg', 'complete_games', 'completeGames'],
      'shutouts': ['sho', 'shutouts'],
      'saves': ['sv', 'saves'],
      'innings_pitched': ['ip', 'innings_pitched', 'inningsPitched'],
      'hits_allowed': ['h', 'hits_allowed', 'hitsAllowed', 'hits'],
      'runs_allowed': ['r', 'runs_allowed', 'runsAllowed', 'runs'],
      'earned_runs': ['er', 'earned_runs', 'earnedRuns'],
      'walks_allowed': ['bb', 'walks_allowed', 'walksAllowed', 'walks'],
      'strikeouts_pitched': ['so', 'strikeouts_pitched', 'strikeoutsPitched', 'strikeouts'],
      'home_runs_allowed': ['hr', 'home_runs_allowed', 'homeRunsAllowed', 'homeRuns'],
      'doubles_allowed': ['doubles_allowed', 'doublesAllowed', 'doubles'],
      'triples_allowed': ['triples_allowed', 'triplesAllowed', 'triples'],
      'wild_pitches': ['wp', 'wild_pitches', 'wildPitches'],
      'hit_batters': ['hbp', 'hit_batters', 'hitBatters', 'hitByPitch'],
      'balks': ['bk', 'balks'],
      'at_bats': ['ab', 'at_bats', 'atBats'],
      'batting_average_against': ['baa', 'batting_average_against', 'battingAverageAgainst', 'battingAverage'],
      'WHIP': ['whip', 'WHIP'],
      'fielding_percentage': ['fpct', 'fielding_percentage', 'fieldingPercentage'],
      'errors': ['e', 'errors'],
      'putouts': ['po', 'putouts'],
      'assists': ['a', 'assists'],
      'double_plays': ['dp', 'double_plays', 'doublePlays'],
      'total_chances': ['tc', 'total_chances', 'totalChances'],
      'stolen_bases_against': ['sba', 'stolen_bases_against', 'stolenBasesAgainst', 'stolenBasesAllowed'],
      'runners_caught_stealing': ['rcs', 'runners_caught_stealing', 'runnersCaughtStealing'],
      'runners_caught_stealing_percentage': ['rcs_pct', 'runners_caught_stealing_percentage', 'runnersCaughtPercentage'],
      'passed_balls': ['pb', 'passed_balls', 'passedBalls'],
      'ops_plus': ['ops_plus', 'OPS+', 'opsPlus', 'ops_plus_adjusted'],
    };

    // Try to get value using fallback keys
    const possibleKeys = fallbackMapping[actualKey] || [actualKey];
    for (const key of possibleKeys) {
      if (stats?.[key] !== undefined && stats?.[key] !== null) {
        return formatStat(stats[key], actualKey);
      }
    }

    return formatStat(stats?.[actualKey], actualKey);
  };

  // Only show loading spinner if there's no data yet (initial load)
  const isInitialLoading = loading && data.length === 0;
  const isLoadingMore = loading && data.length > 0;

  // Get backend field name for a UI column key
  const getBackendField = (uiColumn) => {
    return columnToBackendSort[uiColumn] || statToBackendSort[uiColumn] || uiColumn;
  };

  // Handle sort column click - sends to backend via onSort callback
  const handleSort = (uiColumn) => {
    if (!onSort) return;
    const backendField = getBackendField(uiColumn);
    let newDirection;
    if (sortColumn === backendField) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = 'asc';
    }
    onSort(backendField, newDirection);
  };

  // Render sort icon - checks against backend field name
  const renderSortIcon = (uiColumn) => {
    const backendField = getBackendField(uiColumn);
    if (sortColumn !== backendField) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-cyan-400 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-cyan-400 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isInitialLoading) {
    return (
      <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 rounded-lg bg-slate-900/80">
        <div className="flex items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 rounded-lg bg-slate-900/80">
        <div className="text-center py-12 text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="text-lg font-medium">No players found</p>
          <p className="text-sm mt-2">This team doesn't have any players yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-slate-900/80 relative">
      {/* Loading Overlay */}
      {loading && data.length > 0 && (
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-30 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-sm font-medium">Loading...</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto w-full max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
        <table className="min-w-full text-xs border-collapse">
          <thead className="bg-slate-900/60 sticky top-0 z-10">
            <tr className="text-white">
              <th
                onClick={() => handleSort('no')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                NO.{renderSortIcon('no')}
              </th>
              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap">
                PHOTO
              </th>
              <th
                onClick={() => handleSort('name')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                NAME{renderSortIcon('name')}
              </th>
              <th
                onClick={() => handleSort('position')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                POSITION{renderSortIcon('position')}
              </th>
              <th
                onClick={() => handleSort('class')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                CLASS{renderSortIcon('class')}
              </th>
              <th
                onClick={() => handleSort('height')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                HEIGHT{renderSortIcon('height')}
              </th>
              <th
                onClick={() => handleSort('weight')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                WEIGHT{renderSortIcon('weight')}
              </th>
              <th
                onClick={() => handleSort('batThrow')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                BAT/THROW{renderSortIcon('batThrow')}
              </th>
              {/* <th
                onClick={() => handleSort('playerScore')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                PLAYER SCORE{renderSortIcon('playerScore')}
              </th> */}
              {/* Dynamic stat columns - now sortable */}
              {dynamicStatColumns.map((column, index) => (
                <th
                  key={`stat-${index}`}
                  onClick={() => handleSort(column.statId)}
                  className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap bg-slate-900/60 cursor-pointer hover:bg-slate-800/60 transition-colors"
                >
                  {column.label}{renderSortIcon(column.statId)}
                </th>
              ))}
              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold sticky right-0 bg-slate-900/95 z-20 text-center text-[10px] sm:text-xs">
                {/* ACTIONS */}
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((p, idx) => (
              <PlayerRow
                key={p._id || idx}
                player={p}
                dynamicStatColumns={dynamicStatColumns}
                getStatValue={getStatValue}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {pagination && pagination.hasMore && onLoadMore && (
        <div className="flex justify-center items-center p-4 border-t border-slate-700/50">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Loading...
              </>
            ) : (
              <>
                Load More
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      )}

      {/* Pagination Info + Limit Selector */}
      {pagination && (
        <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-slate-700/50">
          <span className="text-xs text-gray-400">
            Showing {data.length} of {pagination.totalPlayers || pagination.totalCount || 0} players
          </span>
          {onLimitChange && (
            <SimpleDropdown
              value={limit}
              onChange={(val) => onLimitChange(Number(val))}
              options={[
                { label: '10 / page', value: 10 },
                { label: '20 / page', value: 20 },
                { label: '50 / page', value: 50 },
                { label: '100 / page', value: 100 },
              ]}
              variant="admin"
              dropUp
            />
          )}
        </div>
      )}
    </div>
  );
};
